import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import AppConfig from "../../../../../config/AppConfig";
import Page from "../../../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../../../components/FontAwesomeIcons";
import {AppIconThumbnail} from "../../../../../components/BaseComponents/AppIconThumbnail";
import FuxOffsetPaginator from "../../../../../components/BaseComponents/FuxOffsetPaginator/FuxOffsetPaginator";
import ModalPortalHOC from "../../../../../components/BaseComponents/ModalPortalHOC";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import {swal, swalConfirm, swalError, swalLoading, swalSuccess} from "../../../../../helpers/SweetAlertWrapper";
import {
    AWAY_TRIP_STATUS_APPROVED,
    AWAY_TRIP_STATUS_BADGE_CLASSES, AWAY_TRIP_STATUS_COMPLETED, AWAY_TRIP_STATUS_IN_REVIEW,
    AWAY_TRIP_STATUS_REQUESTED,
    AWAY_TRIP_STATUSES
} from "./AwayTripsConstants";
import NewExpensePage from "./NewExpensePage";
import ExpenseDetailPage from "./ExpenseDetailPage";
import {
    EXPENSE_STATUS_BADGE_CLASSES,
    EXPENSE_STATUS_WAITING_REVIEW,
    EXPENSE_STATUSES,
    EXPENSE_TYPES
} from "./ExpensesConstants";

export default class AwayTripDetailPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            trip: null,
            logsVersion: 0,
            newExpenseOpen: false,
            detailExpense: null
        };
        this.expensesPaginatorRef = React.createRef();
    }

    componentDidMount() {
        this.fetchDetails();
    }

    componentDidUpdate(prevProps) {
        const prevTripId = prevProps.trip?.trip_id;
        const nextTripId = this.props.trip?.trip_id;
        if (prevTripId !== nextTripId) {
            this.fetchDetails();
        }
    }

    getStatusInfo = status => ({
        label: AWAY_TRIP_STATUSES[status] || AWAY_TRIP_STATUSES[AWAY_TRIP_STATUS_REQUESTED],
        className: AWAY_TRIP_STATUS_BADGE_CLASSES[status] || AWAY_TRIP_STATUS_BADGE_CLASSES[AWAY_TRIP_STATUS_REQUESTED]
    });

    getStaffById = staffId => {
        if (!staffId) return null;
        return (this.props.staffList || []).find(staff => String(staff.id_dipendente) === String(staffId));
    }

    fetchDetails = _ => {
        const tripId = this.props.trip?.trip_id;
        if (!tripId) {
            this.setState({loading: false, error: "Trasferta non valida"});
            return;
        }
        this.setState({loading: true, error: null});
        ModuloHRAPI.AwayTrips.details(tripId)
            .then(trip => {
                this.setState({
                    loading: false,
                    error: null,
                    trip: trip || null,
                    logsVersion: this.state.logsVersion + 1
                });
            })
            .catch(error => {
                this.setState({
                    loading: false,
                    error: error || "Non e' stato possibile caricare la trasferta"
                });
            });
    }

    handleDeleteTrip = _ => {
        const trip = this.state.trip || {};
        if (!trip.trip_id) return;
        swalConfirm("Vuoi eliminare questa trasferta?")
            .then(_ => {
                swalLoading();
                return ModuloHRAPI.AwayTrips.delete(trip.trip_id)
                    .then(_ => {
                        swal.safeClose();
                        swalSuccess("Trasferta eliminata");
                        if (this.props.onDeleted) {
                            this.props.onDeleted();
                        }
                        this.props.onDismiss();
                    })
                    .catch(error => {
                        swal.safeClose();
                        swalError(error || "Non e' stato possibile eliminare la trasferta");
                    });
            })
            .catch(_ => null);
    }

    canDeleteTrip = _ => {
        if (!this.state.trip) return false;
        if (this.state.trip.status === AWAY_TRIP_STATUS_IN_REVIEW) return false;
        if (this.state.trip.status === AWAY_TRIP_STATUS_COMPLETED) return false;
        if (this.state.trip.status === AWAY_TRIP_STATUS_APPROVED) return false;
        return true;
    }

    canRequestReview = _ => {
        const trip = this.state.trip || {};
        if (trip.status !== AWAY_TRIP_STATUS_APPROVED || !trip.end_date) {
            return false;
        }
        return moment().startOf("day").isSameOrAfter(moment(trip.end_date).startOf("day"));
    }

    handleRequestReview = _ => {
        const trip = this.state.trip || {};
        if (!trip.trip_id || !this.canRequestReview()) return;
        swalConfirm("Sei sicuro di voler richiedere la revisione di questa trasferta e delle relative spese? Non potrai modificare e aggiungere altre spese durante la revisione.")
            .then(_ => {
                swalLoading();
                ModuloHRAPI.AwayTrips.requestReview(trip.trip_id)
                    .then(message => {
                        swal.safeClose();
                        swalSuccess(message || "Richiesta di approvazione inviata");
                        this.fetchDetails();
                    })
                    .catch(error => {
                        swal.safeClose();
                        swalError(error || "Non e' stato possibile inviare la richiesta di approvazione");
                    });
            })

    }

    handleLogsPageRequest = page => {
        const tripId = this.state.trip?.trip_id;
        if (!tripId) {
            return Promise.resolve({data: [], total: 0, max_items: 1, pages: 1, prev: null, next: null});
        }
        return ModuloHRAPI.AwayTrips.logsPagination(tripId, page);
    }

    handleOpenNewExpense = _ => this.setState({newExpenseOpen: true});
    handleCloseNewExpense = _ => this.setState({newExpenseOpen: false});
    handleExpenseCreated = _ => {
        this.setState({newExpenseOpen: false}, this.resetTripExpensesPaginator);
    }
    handleExpenseDetailClose = _ => this.setState({detailExpense: null});

    resetTripExpensesPaginator = _ => {
        if (this.expensesPaginatorRef.current && this.expensesPaginatorRef.current.reset) {
            this.expensesPaginatorRef.current.reset();
        }
    }

    handleTripExpensesPageRequest = page => {
        const tripId = this.state.trip?.trip_id;
        if (!tripId) {
            return Promise.resolve({data: [], total: 0, max_items: 1, pages: 1, prev: null, next: null});
        }
        return ModuloHRAPI.Expenses.pagination(null, page, null, null, tripId);
    }

    handleExpenseOpen = expense => {
        const staff = this.getStaffById(expense.id_dipendente) || this.props.staff || null;
        this.setState({detailExpense: {expense: expense, staff: staff}});
    }

    handleExpenseUpdated = _ => {
        this.resetTripExpensesPaginator();
    }

    handleExpenseDeleted = _ => {
        this.resetTripExpensesPaginator();
    }

    renderTripExpenseItem = (item, index) => {
        const status = item?.status;
        const statusLabel = EXPENSE_STATUSES[status] || EXPENSE_STATUSES[EXPENSE_STATUS_WAITING_REVIEW];
        const statusClassName = EXPENSE_STATUS_BADGE_CLASSES[status] || EXPENSE_STATUS_BADGE_CLASSES[EXPENSE_STATUS_WAITING_REVIEW];
        const typeLabel = EXPENSE_TYPES[item?.type] || EXPENSE_TYPES.generic;
        const amountLabel = item?.amount ? `€ ${item.amount}` : "-";
        const dateTime = item?.updated_at || item?.date || null;
        const formattedDateTime = dateTime ? moment(dateTime).format("DD/MM/YYYY HH:mm") : "-";

        return (
            <button
                key={item.expense_id || index}
                type={"button"}
                className={"list-group-item list-group-item-action text-left"}
                onClick={() => this.handleExpenseOpen(item)}
            >
                <div className={"d-flex justify-content-between align-items-start"}>
                    <div className={"font-weight-bold"}>{typeLabel} - {amountLabel}</div>
                    <span className={statusClassName}>{statusLabel}</span>
                </div>
                <div className={"small text-muted mt-1"}>
                    {formattedDateTime}
                </div>
            </button>
        );
    }

    renderTripExpensesEmptyPage = _ => <div className={"text-muted small"}>Nessuna spesa collegata.</div>;

    renderLogItem = (item, index) => {
        const statusInfo = this.getStatusInfo(item.status);
        const adminName = item.admin_nome || item.admin_cognome ? `${item.admin_nome || ""} ${item.admin_cognome || ""}`.trim() : "Sistema";
        return (
            <div key={item.log_id || index} className={"list-group-item"}>
                <div className={"d-flex justify-content-between align-items-start"}>
                    <span className={statusInfo.className}>{statusInfo.label}</span>
                    <span className={"small text-muted"}>{moment(item.created_at).format("DD/MM/YYYY HH:mm")}</span>
                </div>
                <div className={"small mt-1"}>{item.description || "-"}</div>
                <div className={"small text-muted mt-1"}>{adminName}</div>
            </div>
        );
    }

    renderLogsEmptyPage = _ => <div className={"text-muted small"}>Nessuno storico disponibile.</div>;

    render() {
        if (this.state.loading) {
            return (
                <Page
                    title={"Dettaglio trasferta"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={<div className={"container py-4 text-center text-muted"}>Caricamento trasferta...</div>}
                />
            );
        }

        if (this.state.error || !this.state.trip) {
            return (
                <Page
                    title={"Dettaglio trasferta"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={<div
                        className={"container py-4 text-center text-danger"}>{this.state.error || "Trasferta non trovata"}</div>}
                />
            );
        }

        const trip = this.state.trip || {};
        const staff = this.getStaffById(trip.id_dipendente) || this.props.staff || {};
        const negozio = staff.negozio || null;
        const negozioIconUrl = negozio?.id_negozio
            ? `${AppConfig.webServerUrl}/api/negozio/${negozio.id_negozio}/logoIcon/50`
            : null;
        const statusInfo = this.getStatusInfo(trip.status);
        const vehicleLabel = trip.vehicle_brand ? `${trip.vehicle_brand} ${trip.vehicle_model || ""} - ${trip.vehicle_license_plate || "-"}` : "Nessuno";

        return (
            <Fragment>
                <Page
                    title={"Dettaglio trasferta"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={
                        <div className={"container py-3"}>
                            <div className={"d-flex justify-content-between align-items-start"}>
                                <div className={"d-flex align-items-center"}>
                                    <AppIconThumbnail size={"36px"} src={negozioIconUrl} className={"mr-2"}/>
                                    <div>
                                        <div className={"font-weight-bold"}>{negozio?.nome || "Attivita'"}</div>
                                        <div className={"text-muted small"}>
                                            {moment(trip.start_date).format("DD/MM/YYYY")} - {moment(trip.end_date).format("DD/MM/YYYY")}
                                        </div>
                                    </div>
                                </div>
                                <span className={statusInfo.className}>{statusInfo.label}</span>
                            </div>

                            <div className={"mt-3"}>
                                <div className={"small text-muted"}>Motivo</div>
                                <div>{trip.reason_name || "-"}</div>
                            </div>

                            <div className={"mt-3"}>
                                <div className={"small text-muted"}>Percorso</div>
                                <div>{trip.from_city_name || "-"} - {trip.to_city_name || "-"}</div>
                            </div>

                            <div className={"mt-3"}>
                                <div className={"small text-muted"}>Veicolo</div>
                                <div>{vehicleLabel}</div>
                            </div>

                            {
                                trip.status === AWAY_TRIP_STATUS_APPROVED &&
                                <div className={"mt-4"}>
                                    <button
                                        type={"button"}
                                        className={"btn btn-primary"}
                                        onClick={this.handleOpenNewExpense}
                                    >
                                        Nuova spesa
                                    </button>
                                    {
                                        this.canRequestReview() &&
                                        <button
                                            type={"button"}
                                            className={"btn btn-outline-primary ml-2"}
                                            onClick={this.handleRequestReview}
                                        >
                                            Richiedi approvazione
                                        </button>
                                    }
                                </div>
                            }

                            {
                                this.canDeleteTrip() &&
                                <div className={"mt-4"}>
                                    <button
                                        type={"button"}
                                        className={"btn btn-outline-danger"}
                                        onClick={this.handleDeleteTrip}
                                    >
                                        Elimina trasferta
                                    </button>
                                </div>
                            }

                            <div className={"mt-4"}>
                                <div className={"small text-muted mb-2"}>Spese trasferta</div>
                                <FuxOffsetPaginator
                                    ref={this.expensesPaginatorRef}
                                    onPageRequest={this.handleTripExpensesPageRequest}
                                    onItemRender={this.renderTripExpenseItem}
                                    onEmptyPage={this.renderTripExpensesEmptyPage}
                                    classItemsContainer={"list-group"}
                                    visiblePagesCount={3}
                                />
                            </div>

                            <div className={"mt-4"}>
                                <div className={"small text-muted mb-2"}>Storico stato</div>
                                <FuxOffsetPaginator
                                    key={`away-trip-logs-${trip.trip_id}-${this.state.logsVersion}`}
                                    onPageRequest={this.handleLogsPageRequest}
                                    onItemRender={this.renderLogItem}
                                    onEmptyPage={this.renderLogsEmptyPage}
                                    classItemsContainer={"list-group"}
                                    visiblePagesCount={3}
                                />
                            </div>
                        </div>
                    }
                />
                <ModalPortalHOC>
                    {
                        this.state.newExpenseOpen &&
                        <NewExpensePage
                            active={this.state.newExpenseOpen}
                            onDismiss={this.handleCloseNewExpense}
                            onExpenseCreated={this.handleExpenseCreated}
                            staffList={this.props.staffList}
                            selectedStaffId={trip.id_dipendente ? parseInt(trip.id_dipendente, 10) : null}
                            lockedStaffId={trip.id_dipendente ? parseInt(trip.id_dipendente, 10) : null}
                            tripId={trip.trip_id}
                        />
                    }
                    {
                        this.state.detailExpense &&
                        <ExpenseDetailPage
                            expense={this.state.detailExpense.expense}
                            staff={this.state.detailExpense.staff}
                            active={!!this.state.detailExpense}
                            onDismiss={this.handleExpenseDetailClose}
                            onUpdated={this.handleExpenseUpdated}
                            onDeleted={this.handleExpenseDeleted}
                        />
                    }
                </ModalPortalHOC>
            </Fragment>
        );
    }
}

AwayTripDetailPage.propTypes = {
    active: PropTypes.bool,
    onDismiss: PropTypes.func,
    onDeleted: PropTypes.func,
    trip: PropTypes.object,
    staff: PropTypes.object,
    staffList: PropTypes.array
};
